import {
  BIOMES,
  bootsSpeedBonus,
  type ClassId,
  CLASSES,
  AFFIXES,
  type AffixTotals,
  describeAffix,
  describeAffixRange,
  effectiveBonus,
  getCompanion,
  getItem,
  type ItemInstance,
  type ItemTemplate,
  type ItemUse,
  itemUses,
  mountSpeedBonus,
  RARITY_COLORS,
  upgradeColor,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import type { Comparison } from './item-compare.js';
import { paintItemIcon } from './item-icon.js';
import { sourceLines } from './item-source-text.js';
import { npcName } from './quest-text.js';
import { uiDate, uiDecimal, uiFormat, uiText } from './text.js';

/**
 * One tooltip element for the whole UI, moved and refilled on hover. Text is
 * built with DOM nodes, never innerHTML: item names come from data today, but
 * the habit protects the day they come from players.
 *
 * A finger cannot hover (ADR 0043): on a phone the same card is pinned by a
 * tap next to the item it describes, carries the buttons that replace the
 * mouse gestures, and closes on a tap elsewhere or Escape.
 */

/** A button of a pinned tooltip: what a double-click or a drag does with a mouse. */
export interface TooltipAction {
  label: string;
  run: () => void;
}

/** What a card says beyond the item itself. */
export interface TooltipDetails {
  /** « Obtention »: where to come by it, for the craftsmen's windows. */
  sources?: boolean;
  /** « Comparé à ce que tu portes »: the gain or loss against the piece worn in its slot. */
  compare?: Comparison;
  /**
   * Sum of each affix over everything worn (ADR 0057), for a worn piece: an
   * affix whose sum passes its ceiling is greyed, its excess counts for nothing.
   */
  wornTotals?: AffixTotals;
  /**
   * The item's own icon beside its name: for a link in the chat, where
   * nothing else on screen shows what the piece looks like. Null while the
   * atlas is missing: the card then goes without.
   */
  icon?: CssAtlas | null;
}

/** Room kept between a pinned card and the screen's edges or notch. */
const PIN_MARGIN = 8;

const RARITY_LABELS = {
  common: 'Commun',
  rare: 'Rare',
  epic: 'Épique',
  legendary: 'Légendaire',
} as const;

export const SLOT_LABELS = {
  helmet: 'Casque',
  chest: 'Plastron',
  gloves: 'Gants',
  boots: 'Bottes',
  weapon: 'Arme',
  offhand: 'Main gauche',
  pet: 'Familier',
  mount: 'Monture',
  wings: 'Ailes',
  trail: 'Traînée',
} as const;

export const STAT_LABELS = {
  health: 'Vie',
  defence: 'Défense',
  dps: 'DPS',
} as const;

/** « 14,2 » or « 14.2 »: dps is counted in tenths (ADR 0015) and always shows its one decimal. */
export function formatDps(value: number): string {
  return uiDecimal(value, 1);
}

/** A stat's value as the interface prints it. */
export function formatStat(stat: keyof typeof STAT_LABELS, value: number): string {
  return stat === 'dps' ? formatDps(value) : String(value);
}

/** One line per use, the way a player asks it: who takes it, and for what. */
function useLine(use: ItemUse): { icon: string; text: string } {
  switch (use.kind) {
    case 'alchemy':
      return {
        icon: 'alchemy',
        text: uiFormat('Alchimiste : {potion} (×{quantity})', {
          potion: getItem(use.potion)?.name ?? use.potion,
          quantity: use.quantity,
        }),
      };
    case 'upgrade':
      return {
        icon: 'stat-upgrade',
        text: uiFormat('Forgeron : améliore les pièces de la région {region} (+{from} à +{to})', {
          region: BIOMES[use.biome].name,
          from: use.from,
          to: use.to,
        }),
      };
    case 'fusion': {
      // The next rarity keeps the name: only its label tells them apart.
      const result = getItem(use.result);
      return {
        icon: 'smelter',
        text:
          result === undefined
            ? uiFormat('Fondeur : 5 identiques donnent {result}', { result: use.result })
            : uiFormat('Fondeur : 5 identiques donnent {result} ({rarity})', {
                result: result.name,
                rarity: uiText(RARITY_LABELS[result.rarity]),
              }),
      };
    }
  }
}

function hex(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

export class ItemTooltip {
  private readonly root: HTMLElement;
  /** The element a pinned card describes: a tap on it is not a tap elsewhere. */
  private anchor: HTMLElement | null = null;
  private onClose: (() => void) | undefined;
  private readonly onOutside = (event: PointerEvent): void => {
    if (!(event.target instanceof Node)) return;
    if (this.root.contains(event.target) || this.anchor?.contains(event.target) === true) return;
    this.hide();
  };
  // Capture on window runs before every panel's own Escape: the first
  // Escape closes the card, the next one the window under it.
  private readonly onEscape = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape' || this.anchor === null) return;
    event.stopPropagation();
    this.hide();
  };

  constructor() {
    this.root = document.createElement('div');
    this.root.className = 'tooltip';
    this.root.hidden = true;
    document.body.append(this.root);
  }

  /** Whether a tap pinned the card: hover events then leave it alone. */
  get pinned(): boolean {
    return this.anchor !== null;
  }

  /** Whether the card is pinned beside `anchor`. */
  pinnedTo(anchor: HTMLElement): boolean {
    return this.anchor === anchor;
  }

  show(
    template: ItemTemplate,
    instance: ItemInstance | undefined,
    wearer: { level: number; classId: ClassId },
    at: { x: number; y: number },
    details: TooltipDetails = {},
  ): void {
    if (this.pinned) return;
    this.fill(template, instance, wearer, details);
    this.root.hidden = false;
    this.moveTo(at);
  }

  /**
   * The touch tooltip: beside `anchor`, kept whole on screen, with `actions`
   * under the text. `onClose` runs once when it goes, however it goes.
   */
  pin(
    template: ItemTemplate,
    instance: ItemInstance | undefined,
    wearer: { level: number; classId: ClassId },
    anchor: HTMLElement,
    actions: readonly TooltipAction[] = [],
    onClose?: () => void,
    details: TooltipDetails = {},
  ): void {
    this.hide();
    this.fill(template, instance, wearer, details);
    if (actions.length > 0) {
      const row = document.createElement('div');
      row.className = 'tooltip__actions';
      for (const action of actions) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'tooltip__action';
        button.textContent = uiText(action.label);
        button.addEventListener('click', () => {
          this.hide();
          action.run();
        });
        row.append(button);
      }
      this.root.append(row);
    }
    this.anchor = anchor;
    this.onClose = onClose;
    this.root.classList.add('tooltip--pinned');
    this.root.hidden = false;
    this.placeBeside(anchor.getBoundingClientRect());
    document.addEventListener('pointerdown', this.onOutside, true);
    window.addEventListener('keydown', this.onEscape, true);
  }

  /** Beside the item where the screen has room, else above or under it; never off screen. */
  private placeBeside(rect: DOMRect): void {
    const width = this.root.offsetWidth;
    const height = this.root.offsetHeight;
    const maxLeft = window.innerWidth - width - PIN_MARGIN;
    const maxTop = window.innerHeight - height - PIN_MARGIN;
    let left: number;
    let top: number;
    if (rect.right + PIN_MARGIN + width <= window.innerWidth - PIN_MARGIN) {
      left = rect.right + PIN_MARGIN;
      top = rect.top;
    } else if (rect.left - PIN_MARGIN - width >= PIN_MARGIN) {
      left = rect.left - PIN_MARGIN - width;
      top = rect.top;
    } else {
      left = rect.left + rect.width / 2 - width / 2;
      top =
        rect.top - PIN_MARGIN - height >= PIN_MARGIN
          ? rect.top - PIN_MARGIN - height
          : rect.bottom + PIN_MARGIN;
    }
    this.root.style.left = `${String(Math.round(Math.max(PIN_MARGIN, Math.min(left, maxLeft))))}px`;
    this.root.style.top = `${String(Math.round(Math.max(PIN_MARGIN, Math.min(top, maxTop))))}px`;
  }

  private fill(
    template: ItemTemplate,
    instance: ItemInstance | undefined,
    wearer: { level: number; classId: ClassId },
    details: TooltipDetails,
  ): void {
    this.root.replaceChildren();
    const line = (text: string, className?: string): void => {
      const element = document.createElement('div');
      if (className !== undefined) element.className = className;
      element.textContent = uiText(text);
      this.root.append(element);
    };
    // The craftsman's own HUD icon (`icon-<name>.png`) in front of what he makes of it.
    const useRow = (icon: string, text: string): void => {
      const row = document.createElement('div');
      row.className = 'tooltip__use';
      const image = document.createElement('span');
      image.className = 'tooltip__use-icon';
      image.style.backgroundImage = `url('/assets/ui/icon-${icon}.png')`;
      const label = document.createElement('span');
      label.className = 'tooltip__use-text';
      label.textContent = uiText(text);
      row.append(image, label);
      this.root.append(row);
    };

    const name = document.createElement('div');
    name.className = 'tooltip__name';
    name.style.color = hex(RARITY_COLORS[template.rarity]);
    name.textContent = uiText(
      instance !== undefined && instance.quantity > 1
        ? `${template.name} ×${String(instance.quantity)}`
        : template.name,
    );
    // The smith's « +N » after the name; from +3 in the colour of its level.
    const level = instance?.upgradeLevel ?? 0;
    if (level > 0) {
      const plus = document.createElement('span');
      plus.textContent = ` +${String(level)}`;
      const color = upgradeColor(level);
      if (color !== undefined) plus.style.color = hex(color);
      name.append(plus);
    }
    const atlas = details.icon ?? null;
    if (atlas !== null && atlas.rect(template.icon) !== null) {
      const head = document.createElement('div');
      head.className = 'tooltip__head';
      const icon = document.createElement('span');
      icon.className = 'tooltip__icon';
      paintItemIcon(icon, template, atlas, template.name);
      head.append(icon, name);
      this.root.append(head);
    } else {
      this.root.append(name);
    }

    const kind =
      template.slot !== undefined
        ? SLOT_LABELS[template.slot]
        : template.kind === 'consumable'
          ? 'Consommable'
          : 'Matériau';
    const rarity = uiText(RARITY_LABELS[template.rarity]);
    line(
      template.cosmetic
        ? uiFormat('{rarity} · Cosmétique · {kind}', { rarity, kind: uiText(kind) })
        : uiFormat('{rarity} · {kind}', { rarity, kind: uiText(kind) }),
      'tooltip__muted',
    );
    if (template.cosmetic) {
      line('Se porte par-dessus ton équipement, sans en changer les stats', 'tooltip__muted');
    }

    if (template.levelRequired > 1) {
      line(
        uiFormat('Niveau {level} requis', { level: template.levelRequired }),
        wearer.level < template.levelRequired ? 'tooltip__warn' : 'tooltip__muted',
      );
    }
    if (template.classes.length > 0) {
      const names = template.classes.map((id) => CLASSES[id].name).join(', ');
      line(names, template.classes.includes(wearer.classId) ? 'tooltip__muted' : 'tooltip__warn');
    }
    // What the piece gives at its level, not the template's bare stats.
    const bonus = effectiveBonus(template.bonus, level);
    for (const stat of ['dps', 'defence', 'health'] as const) {
      if (bonus[stat] === 0) continue;
      line(
        uiFormat('+{value} {stat}', {
          value: formatStat(stat, bonus[stat]),
          stat: uiText(STAT_LABELS[stat]),
        }),
        'tooltip__bonus',
      );
    }
    // The boots' fixed substat (ADR 0059): rarity and level set it, never the smith.
    const speed = bootsSpeedBonus(template);
    if (speed > 0) {
      line(uiFormat('+{value} % de vitesse de déplacement', { value: speed }), 'tooltip__bonus');
    }
    // The affixes (ADR 0057), apart from the stats: the smith's level never multiplies them.
    // Each with the lowest and highest roll it may have on this piece.
    for (const affix of instance?.affixes ?? []) {
      const cap = AFFIXES[affix.id].cap;
      const total = details.wornTotals?.[affix.id] ?? 0;
      const capped = cap !== undefined && total > cap;
      const element = document.createElement('div');
      element.className = capped ? 'tooltip__affix tooltip__affix--capped' : 'tooltip__affix';
      const notes = `${affix.sealed === true ? uiText(' (scellée)') : ''}${capped ? uiText(' (plafond atteint)') : ''}`;
      element.textContent = uiFormat('{affix}{notes}', { affix: describeAffix(affix), notes });
      const range = document.createElement('span');
      range.className = 'tooltip__range';
      range.textContent = uiFormat(' [{range}]', { range: describeAffixRange(affix.id, template) });
      element.append(range);
      this.root.append(element);
    }
    const mount = template.companion === undefined ? undefined : getCompanion(template.companion);
    if (mount?.kind === 'mount') {
      line(
        uiFormat('+{value} % de vitesse de déplacement', { value: mountSpeedBonus(mount) }),
        'tooltip__bonus',
      );
    }
    if (template.heal !== undefined)
      line(uiFormat('Rend {heal} points de vie', { heal: template.heal }), 'tooltip__bonus');
    if (template.bait !== undefined) {
      line(
        uiFormat(
          'À utiliser près d’un terrier ({den}) : fait sortir {summons}, pour toi et ton groupe',
          { den: npcName(template.bait.den), summons: npcName(template.bait.summons) },
        ),
        'tooltip__bonus',
      );
    }
    if (details.compare !== undefined) {
      const { worn, deltas } = details.compare;
      line('Comparé à ce que tu portes', 'tooltip__heading');
      if (worn === null) {
        line('Rien de porté à cet emplacement', 'tooltip__muted');
      } else {
        const plus = worn.upgradeLevel > 0 ? ` +${String(worn.upgradeLevel)}` : '';
        line(
          uiFormat('Porté : {name}{plus}', { name: worn.template.name, plus }),
          'tooltip__muted',
        );
      }
      for (const { stat, delta } of deltas) {
        // A plain hyphen: the generated font has no minus sign.
        const label = uiText(STAT_LABELS[stat]);
        const text =
          delta === 0
            ? uiFormat('= {stat}', { stat: label })
            : uiFormat('{sign}{value} {stat}', {
                sign: delta > 0 ? '+' : '-',
                value: formatStat(stat, Math.abs(delta)),
                stat: label,
              });
        line(text, delta > 0 ? 'tooltip__bonus' : delta < 0 ? 'tooltip__warn' : 'tooltip__muted');
      }
      const speedDelta = speed - (worn === null ? 0 : bootsSpeedBonus(worn.template));
      if (speed > 0 && speedDelta !== 0) {
        line(
          uiFormat('{sign}{value} % de vitesse de déplacement', {
            sign: speedDelta > 0 ? '+' : '-',
            value: Math.abs(speedDelta),
          }),
          speedDelta > 0 ? 'tooltip__bonus' : 'tooltip__warn',
        );
      }
      for (const { id, delta } of details.compare.affixes) {
        const text = describeAffix({ id, value: Math.abs(delta) });
        if (delta > 0) line(text, 'tooltip__bonus');
        else line(uiFormat('Perd {affix}', { affix: text }), 'tooltip__warn');
      }
    }
    // « À quoi ça sert ? », as in Dofus: what the crafting tables take it for.
    if (!template.cosmetic) {
      const uses = itemUses(template);
      if (uses.length > 0) {
        line('Utilisation', 'tooltip__heading');
        for (const use of uses) {
          const { icon, text } = useLine(use);
          useRow(icon, text);
        }
      } else if (template.kind === 'material' && template.sellPrice > 0) {
        line('Utilisation', 'tooltip__heading');
        useRow('merchant', 'Aucun artisan ne s’en sert : à vendre au marchand');
      }
    }
    if (details.sources === true) {
      const sources = sourceLines(template.id);
      if (sources.length > 0) {
        line('Obtention', 'tooltip__heading');
        for (const source of sources) useRow(source.icon, source.text);
      }
    }
    // What "bound" means, spelled out (SHE-44): the server refuses the rest.
    if (instance?.bound === true) {
      line('Objet lié à ton personnage', 'tooltip__muted');
      line(
        'Il ne s’échange pas, ne passe ni à l’Hôtel des ventes ni au Coffre du compte. Le marchand le rachète.',
        'tooltip__muted',
      );
    }
    // A shop purchase waits before it changes hands (ADR 0044); the server decides, this only says when.
    const tradableAfter = instance?.tradableAfter;
    if (tradableAfter !== undefined && Date.parse(tradableAfter) > Date.now()) {
      const date = uiDate(tradableAfter, { day: 'numeric', month: 'long' });
      line(uiFormat('Échangeable à partir du {date}', { date }), 'tooltip__muted');
    }
    if (template.sellPrice > 0) {
      line(uiFormat('Vente : {price} or', { price: template.sellPrice }), 'tooltip__muted');
    }
  }

  moveTo(at: { x: number; y: number }): void {
    if (this.pinned) return;
    // Keep it on screen: flip to the left of the cursor near the right edge.
    const width = this.root.offsetWidth;
    const left = at.x + 14 + width > window.innerWidth ? at.x - width - 8 : at.x + 14;
    this.root.style.left = `${String(Math.max(4, left))}px`;
    this.root.style.top = `${String(Math.max(4, at.y + 12))}px`;
  }

  hide(): void {
    this.root.hidden = true;
    if (this.anchor === null) return;
    this.anchor = null;
    this.root.classList.remove('tooltip--pinned');
    document.removeEventListener('pointerdown', this.onOutside, true);
    window.removeEventListener('keydown', this.onEscape, true);
    const onClose = this.onClose;
    this.onClose = undefined;
    onClose?.();
  }

  destroy(): void {
    this.hide();
    this.root.remove();
  }
}
